import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { refPayload } from '../../lib/format';
import { Field, ConfirmModal, FormModal, messaggioErrore } from './common';

const VUOTO = { identificativo: '', piano: '', note: '' };

/**
 * Appartamenti di una palazzina.
 *
 * Vivono nella pagina della palazzina perche' le appartengono: l'appartamento
 * e' quello che lega un condomino a un'unita' immobiliare, e senza quel
 * legame il BookingEngine non lo lascia prenotare.
 */
export default function AppartamentiPalazzina({ palazzina, appartamenti, onChanged }) {
    const [form, setForm] = useState(null);
    const [inModifica, setInModifica] = useState(null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(null);
    const [working, setWorking] = useState(false);

    const ordinati = useMemo(
        () => [...appartamenti].sort((a, b) =>
            String(a.identificativo).localeCompare(String(b.identificativo), 'it', { numeric: true })),
        [appartamenti]
    );

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    function apriNuovo() {
        setInModifica(null);
        setForm({ ...VUOTO });
    }

    function apriModifica(a) {
        setInModifica(a);
        setForm({
            identificativo: a.identificativo || '',
            piano: a.piano || '',
            note: a.note || ''
        });
    }

    async function salva(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!form.identificativo.trim()) {
            toast.error("L'identificativo è obbligatorio.", { theme: 'colored' });
            return;
        }

        const payload = {
            // la palazzina non e' una scelta: e' quella della pagina
            idPalazzina: refPayload(palazzina.id),
            identificativo: form.identificativo.trim(),
            piano: form.piano,
            note: form.note
        };

        setSaving(true);
        try {
            const res = inModifica
                ? await DataManager.updateItem('appartamento', payload, inModifica.id)
                : await DataManager.insertItem('appartamento', payload);
            if (res && res.success === 1) {
                toast.success(inModifica ? 'Appartamento aggiornato.' : 'Appartamento creato.', { theme: 'colored' });
                setForm(null);
                setInModifica(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('appartamento', daEliminare.id);
            if (res && res.success === 1) {
                toast.success('Appartamento eliminato.', { theme: 'colored' });
                setDaEliminare(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    return (
        <div className="lv-subsection">
            <div className="lv-subsection-head">
                <h3 className="lv-subsection-title">Appartamenti</h3>
                <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm" onClick={apriNuovo}>
                    <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
                    Aggiungi appartamento
                </button>
            </div>

            {ordinati.length === 0 ? (
                <div className="lv-note">
                    Nessun appartamento in questa palazzina: finché non ce n'è, i suoi condomini
                    non hanno un&apos;unità da associare e non possono prenotare.
                </div>
            ) : (
                <div className="lv-table-wrap">
                    <table className="lv-table">
                        <thead>
                            <tr>
                                <th>Identificativo</th>
                                <th>Piano</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordinati.map(a => (
                                <tr key={a.id}>
                                    <td data-label="Identificativo">
                                        <div className="lv-cell-strong">{a.identificativo}</div>
                                        {a.note && <div className="lv-cell-sub">{a.note}</div>}
                                    </td>
                                    <td data-label="Piano">{a.piano || '—'}</td>
                                    <td data-label="Azioni">
                                        <div className="lv-row-actions">
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm"
                                                onClick={() => apriModifica(a)}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm lv-btn-danger-ghost"
                                                onClick={() => setDaEliminare(a)}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <FormModal
                show={Boolean(form)}
                title={inModifica ? `Appartamento «${inModifica.identificativo}»` : 'Nuovo appartamento'}
                onClose={() => setForm(null)}
                onSubmit={salva}
                saving={saving}
                submitLabel={inModifica ? 'Salva appartamento' : 'Crea appartamento'}
            >
                {form && (
                    <>
                        <Field label="Identificativo" required hint="Es. «A3», «Interno 12».">
                            <input
                                className="lv-input"
                                value={form.identificativo}
                                onChange={e => aggiorna('identificativo', e.target.value)}
                            />
                        </Field>
                        <Field label="Piano">
                            <input
                                className="lv-input"
                                value={form.piano}
                                onChange={e => aggiorna('piano', e.target.value)}
                            />
                        </Field>
                        <Field label="Note" wide>
                            <textarea
                                className="lv-input"
                                rows={2}
                                value={form.note}
                                onChange={e => aggiorna('note', e.target.value)}
                            />
                        </Field>
                    </>
                )}
            </FormModal>

            <ConfirmModal
                show={Boolean(daEliminare)}
                title="Eliminare l'appartamento?"
                message={daEliminare
                    ? `«${daEliminare.identificativo}» verrà rimosso dall'anagrafica. I condomini che vi erano associati restano senza unità.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(null)}
            />
        </div>
    );
}
